import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Crest } from "../components/Media";

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">{label}</span>
    <input {...props} className="mt-2 bg-transparent border-b border-white/20 rounded-none px-0 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] transition-colors w-full font-body" />
  </label>
);

export default function Welcome() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", creating: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [formToken, setFormToken] = useState("");

  useEffect(() => {
    let alive = true;
    api.get("/invite-requests/form-token")
      .then(({ data }) => { if (alive) setFormToken(data.token); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.creating.trim()) {
      setError("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      let token = formToken;
      if (!token) {
        try { const { data } = await api.get("/invite-requests/form-token"); token = data.token; setFormToken(token); } catch {}
      }
      await api.post("/invite-requests", {
        name: form.name.trim(),
        email: form.email.trim(),
        creating: form.creating.trim(),
        website: form.website, // honeypot
        form_token: token,
      });
      setDone(true);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden" data-testid="welcome-page">
      <img src="/logo-crest.png" alt="" className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150vmin] max-w-none opacity-[0.10] pointer-events-none select-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-[#0A0A0A]/20" />

      <div className="relative z-10 min-h-screen flex flex-col">
        <header className="flex items-center gap-3 p-6 md:p-10">
          <Crest className="h-9 w-9" />
          <div className="leading-tight">
            <div className="font-heading text-sm tracking-tight text-white">SOVEREIGN</div>
            <div className="font-body text-[10px] tracking-[0.35em] text-[#14A89A]">SIGNAL</div>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 pb-16">
          <div className="w-full max-w-md">
            {done ? (
              <div className="text-center fade-up" data-testid="welcome-success">
                <CheckCircle2 className="text-[#14A89A] mx-auto mb-5" size={40} strokeWidth={1.5} />
                <h1 className="font-heading text-3xl font-medium text-white mb-3">Request received</h1>
                <p className="font-body text-sm text-zinc-400 leading-relaxed mb-8">
                  Check your email, your private invite key is on its way. Use it to open your application, then the Council reviews each one personally. (First email from us? Peek in your spam folder just in case.)
                </p>
                <button onClick={() => navigate("/")} data-testid="welcome-back-home" className="font-body text-sm text-[#14A89A] hover:text-[#2BC4B5] transition-colors">
                  Back to home
                </button>
              </div>
            ) : (
              <div className="fade-up">
                <div className="font-body text-[10px] tracking-[0.35em] uppercase text-[#14A89A] mb-4">A private network of creators</div>
                <h1 aria-label="An encrypted community and creator platform." className="font-heading text-4xl sm:text-5xl font-medium tracking-tight text-white leading-[1.05]">
                  An encrypted community<br />&amp; creator platform.
                </h1>
                <p className="font-body text-base text-zinc-400 mt-5 leading-relaxed">
                  Sovereign Signal is invite-only and end-to-end encrypted. Tell us a little about yourself and we'll email you a private key to begin your application.
                </p>

                <form onSubmit={submit} className="mt-10 space-y-6">
                  <Field label="Name" value={form.name} onChange={upd("name")} data-testid="welcome-name-input" placeholder="What should we call you?" required />
                  <Field label="Email" type="email" value={form.email} onChange={upd("email")} data-testid="welcome-email-input" placeholder="Where we'll send your invite" required />
                  <label className="block">
                    <span className="font-body text-[10px] font-medium text-zinc-500 tracking-[0.2em] uppercase">What are you creating? Why join?</span>
                    <textarea value={form.creating} onChange={upd("creating")} data-testid="welcome-creating-input" rows={3} maxLength={280} placeholder="A sentence or two on what you're building and why you'd like to be here." className="mt-2 bg-transparent border border-white/15 rounded-none px-3 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#14A89A] transition-colors w-full font-body resize-none" required />
                  </label>

                  {/* Honeypot, hidden from humans, catches bots */}
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={upd("website")}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    data-testid="welcome-honeypot"
                    style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                  />

                  {error && <p className="font-body text-sm text-red-400" data-testid="welcome-error">{error}</p>}

                  <button type="submit" disabled={loading} data-testid="welcome-submit" className="w-full bg-[#14A89A] text-[#0A0A0A] font-medium px-6 py-4 hover:bg-[#2BC4B5] transition-colors flex items-center justify-center gap-2 font-body disabled:opacity-50">
                    {loading ? <Loader2 className="animate-spin" size={18} /> : <>Request an invite <ArrowRight size={16} /></>}
                  </button>
                </form>

                <div className="mt-8 flex items-center gap-2 text-zinc-600 font-body text-xs">
                  <ShieldCheck size={14} className="text-[#14A89A]/70 shrink-0" />
                  <span className="min-w-0">End-to-end encrypted. We never read or sell your content.</span>
                </div>
                <button onClick={() => navigate("/")} data-testid="welcome-signin-link" className="mt-6 font-body text-sm text-zinc-500 hover:text-white transition-colors">
                  Already a member? Sign in
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
